import {
  dateSchema,
  dateTypeSchema,
  Invoice,
  invoiceItemSchema,
  InvoiceSchema,
  objectIdSchema,
  Order,
  orderPODCopyStatusSchema,
  paginationSchema,
  paginationWithSearchSchema,
  priceSchema,
  taxIdSchema,
  invoiceBeneficiarySchema,
  invoiceStatusSchema,
  optionalStringSchema,
  nameSchema,
  Transaction,
  imageWithUploadSchema,
  AccountTransaction,
} from '@hero/schema';
import type { Currency, Id, OrderPODStatus } from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';
import { createPaymentWithBankTransferBaseSchema } from './payment.api';

//* item
const invoiceItemAPISchema = invoiceItemSchema.omit({ amount: true }).extend({
  /** the total amount to be paid */
  total: priceSchema,
  /** if empty, then the invoice is without Tax. This will mainly when dealing with freelancers drivers */
  taxId: taxIdSchema.optional(),
});
export type InvoiceItemAPI = z.infer<typeof invoiceItemAPISchema>;

//* Beneficiary
const invoiceBeneficiaryAPISchema = invoiceBeneficiarySchema.pick({ _id: true, target: true });
export type InvoiceBeneficiaryAPI = z.infer<typeof invoiceBeneficiaryAPISchema>;

//* schema
const baseSchema = InvoiceSchema.model
  .pick({
    _id: true,
    status: true,
    period: true,
  })
  .extend({
    /** the total amount to be paid (THIS IS `AFTER` TAX).*/
    total: priceSchema,
    /** the breakdown of items that make up the total `amount` of the invoice.
     *
     * If multi orders for 1 invoice, then it will be aggregated in 1 item and the type will be `order_vehicle`
     */
    items: z.array(invoiceItemAPISchema),
    /** The party that is going to `PAY` on this invoice (the buyer). */
    buyer: invoiceBeneficiaryAPISchema,
    /** The party that is going to `RECEIVE` payment for this invoice (the seller). */
    seller: invoiceBeneficiaryAPISchema,
  });

//* API - get one
export const invoiceGetOneAPISchema = z.object({
  _id: objectIdSchema.describe('`Invoice._id` (24-char Mongo ObjectId).'),
});
export type InvoiceGetOneAPI = API<z.infer<typeof invoiceGetOneAPISchema>, Invoice>;

//* API - get many
export const invoiceGetManyAPISchema = baseSchema
  .extend(paginationSchema.shape)
  .pick({ status: true })
  .partial();
export type InvoiceGetManyAPI = z.infer<typeof invoiceGetManyAPISchema>;

//* API - create
export const InvoiceCreateAPISchema = baseSchema
  .pick({
    total: true,
    items: true,
    buyer: true,
    seller: true,
  })
  .extend({
    /** we will use that date to generate the invoice period (duration, from, to, due, issuance) depending on the agreement.
     *
     * If there is no agreement, then we use it as Due date and generate the invoice period.
     *
     * If empty, we will use the current date as the target date.
     */
    targetDate: dateSchema.optional(),

    /** if true, we will ignore the agreement and use the target date to generate the invoice period. */
    ignoreAgreement: z.boolean().optional(),
  });
export type InvoiceCreateAPI = API<z.infer<typeof InvoiceCreateAPISchema>, Invoice>;

//* Invoice Unified Status — combines raw statuses with virtual filter groups
export const invoiceUnifiedStatusSchema = z
  .enum([
    ...invoiceStatusSchema.options,
    'not_paid_or_partial',
    'not_paid_or_partial_or_pending',
    'overdue',
  ])
  .describe(
    "Unified invoice status: raw `InvoiceStatus` values + virtual groups. 'not_paid_or_partial' = not_paid OR partially_paid. 'not_paid_or_partial_or_pending' = adds pending. 'overdue' = not cancelled, not paid, past `period.due`.",
  );
export type InvoiceUnifiedStatus = z.infer<typeof invoiceUnifiedStatusSchema>;

//* Invoice Query — standalone query schema (same pattern as ordersQueryAPISchema)
export const invoicesQueryAPISchema = z.object({
  status: invoiceUnifiedStatusSchema
    .optional()
    .describe('Filter by invoice unified status (raw + virtual groups).'),
  dueDate: dateTypeSchema
    .optional()
    .describe(
      "Simple date-window filter on `period.due` (today, this_week, this_month, last_week, etc.). Use 'this_month' for 'invoices due this month'.",
    ),
  clientId: objectIdSchema
    .optional()
    .describe(
      'Filter to invoices issued to a specific CRM Client on the buyer side (`Client._id`). Resolve from `search_clients` when the user names a client.',
    ),
  buyerOrgId: objectIdSchema
    .optional()
    .describe(
      "Filter to invoices where this Org is the buyer (`Invoice.buyer.ids[].id` with type='org_id'). Useful when the seller knows the partner Org id but no CRM Client exists yet.",
    ),
  accountId: objectIdSchema
    .optional()
    .describe(
      'Scope to invoices issued against a single bilateral `Account` document (`Invoice.accountId`).',
    ),
  financeableOnly: z.coerce
    .boolean()
    .optional()
    .describe(
      "When true, narrows to invoices eligible to be financed: status in {not_paid, partially_paid}, no existing active `FinancingApplication`. Use for the 'finance an invoice' flow.",
    ),
});
export type InvoicesQueryAPI = z.infer<typeof invoicesQueryAPISchema>;

//* API - get current User
export const InvoiceGetCurrentUserAPISchema = paginationWithSearchSchema.extend({
  ...invoicesQueryAPISchema.shape,
});
export type InvoiceGetCurrentUserAPI = API<
  z.infer<typeof InvoiceGetCurrentUserAPISchema>,
  Invoice[]
>;

//* API - Get Invoice Orders
export const invoiceOrdersAPISchema = paginationSchema.extend({
  _id: objectIdSchema.describe('`Invoice._id` (24-char ObjectId) to fetch linked orders for.'),
});
export type InvoiceOrdersAPI = API<z.infer<typeof invoiceOrdersAPISchema>, Order[]>;

// ========admin apis========
export const invoiceGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** filter by organization ID — Org appears as either the buyer or the seller party */
  orgId: objectIdSchema.optional(),
  /** filter by the CRM Client party on either side of the invoice */
  clientId: objectIdSchema.optional(),
  /** filter by the bilateral `Account._id` (single account drawer) */
  accountId: objectIdSchema.optional(),
  /** filter by invoice status */
  status: invoiceStatusSchema.optional(),
  /**
   * Bilateral role filter — the role the org/client filter above
   * plays on returned invoices.
   * - `'buyer'`  — only invoices where the filtered org is buyer
   * - `'seller'` — only invoices where the filtered org is seller
   * - `'any'` (default) — either side
   *
   * Replaces the broker-era `beneficiaryType: 'carrier' | 'customer'`
   * filter, which assumed Hero was a counterparty.
   */
  asRole: z.enum(['buyer', 'seller', 'any']).optional(),
});
export type InvoiceGetAllAdminAPI = API<z.infer<typeof invoiceGetAllAdminAPISchema>, Invoice[]>;

export const invoiceExportAdminAPISchema = z.object({
  /** the invoice ID */
  _id: objectIdSchema,
  /** the export format */
  format: z.enum(['pdf', 'excel']),
});
export type InvoiceExportAdminAPI = API<z.infer<typeof invoiceExportAdminAPISchema>, any>;

// pay invoice (admin targets a specific invoice by ID — uses the base schema + invoiceId)
export const invoicePayAdminAPISchema = createPaymentWithBankTransferBaseSchema
  .pick({ amount: true, currency: true, proofOfTransaction: true })
  .extend({
    /** the invoice ID to pay */
    invoiceId: objectIdSchema,
    /** any admin notes related to the payment */
    notes: optionalStringSchema(nameSchema),
    /** optional custom paid-at date — defaults to now on the backend when omitted */
    customPaidAt: dateSchema.optional(),
  });
export type InvoicePayAdminAPI = API<
  z.infer<typeof invoicePayAdminAPISchema>,
  { paidInvoice: Invoice; transaction: Transaction }
>;

/**
 * Carrier-/buyer-/seller-facing "record a payment" endpoint.
 *
 * Role-neutral — any party listed on `Invoice.partyIds` can record
 * an off-platform payment that closes (or partially closes) the
 * invoice. The backend resolves the caller's org from the JWT and
 * rejects with `payment_invalid_beneficiary_type` if the org isn't
 * on the invoice. Wraps `PaymentService.payInvoice`.
 *
 * Returns the updated `Invoice` + the new `AccountTransaction` so
 * the SPA can update its TanStack Query cache without a refetch.
 */
export const invoicePayAPISchema = z.object({
  /** the invoice id to record a payment against */
  invoiceId: objectIdSchema,
  /** payment amount (positive). Server caps to `amount.remaining`. */
  amount: priceSchema,
  /**
   * Optional date the off-platform payment cleared. Defaults to
   * `now` on the backend. Both `Invoice.paidAt` (when the payment
   * closes the invoice) and `AccountTransaction.createdAt` use this
   * value so back-dated entries stay consistent across both docs.
   */
  paidAt: dateSchema.optional(),
  /**
   * Optional transfer receipt — uploaded transfer screenshot or
   * bank-statement PDF. Same shape as the carrier-side VAT proof
   * upload (`imageWithUploadSchema`), so the file-encoding helper
   * and the image-service plumbing are reused verbatim. The image
   * lands on `AccountTransaction.images[]` so future financing
   * applications carry an audit trail.
   */
  proofImage: imageWithUploadSchema.optional(),
  /** optional payer notes */
  notes: optionalStringSchema(nameSchema),
});
export type InvoicePayAPI = API<
  z.infer<typeof invoicePayAPISchema>,
  { paidInvoice: Invoice; transaction: AccountTransaction }
>;

// upload invoice proof (shared schema — used by admin and carrier endpoints)
export const invoiceUploadProofBaseSchema = z.object({
  /** the invoice ID */
  invoiceId: objectIdSchema,
  /** invoice image as a pdf file */
  image: imageWithUploadSchema,
  /** the reference number of the invoice */
  refNumber: z.coerce.string().trim(),
  /**
   * optional override for `Invoice.period.due` — the user-supplied
   * "expected pay date" coming from the carrier-dashboard upload
   * banner. Auto-captured invoices already get a `period.due` from
   * the Agreement / payment terms; this lets manual-capture invoices
   * be corrected at upload time without a separate "edit invoice"
   * surface. Only honoured when the caller is the invoice seller.
   */
  expectedDueDate: dateSchema.optional(),
});

// admin upload invoice proof
export const invoiceUploadProofAdminAPISchema = invoiceUploadProofBaseSchema;
export type InvoiceUploadProofAdminAPI = API<
  z.infer<typeof invoiceUploadProofAdminAPISchema>,
  Invoice
>;

// carrier upload invoice proof
export const invoiceUploadProofCarrierAPISchema = invoiceUploadProofBaseSchema;
export type InvoiceUploadProofCarrierAPI = API<
  z.infer<typeof invoiceUploadProofCarrierAPISchema>,
  Invoice
>;

// admin review invoice proof (approve/reject)
export const invoiceProofReviewAdminAPISchema = z.object({
  invoiceId: objectIdSchema,
  status: z.enum(['approved', 'rejected']),
  note: z.coerce.string().trim().optional(),
});
export type InvoiceProofReviewAdminAPI = API<
  z.infer<typeof invoiceProofReviewAdminAPISchema>,
  Invoice
>;

// close invoice (admin)
export const invoiceCloseAdminAPISchema = z.object({
  /** the invoice ID to close */
  invoiceId: objectIdSchema,
  /** if true, closes regardless of period end date or active orders. Default to true */
  forceClose: z.boolean().optional(),
});
export type InvoiceCloseAdminAPI = API<z.infer<typeof invoiceCloseAdminAPISchema>, Invoice>;

// ======== capture legs (admin) ========

/**
 * "Create invoice(s) from selected legs" — the admin manual-capture
 * flow. The backend resolves each `(orderId, contractId)` pair to a
 * delivered, uncaptured leg and runs `captureOrderLeg` on it. Returns
 * the invoices it created plus the legs it skipped (with reason).
 *
 * One invoice per leg. The "select multiple orders" UI just batches
 * the per-leg calls. Multiple invoices CAN be settled in one tx
 * later via the regular pay flow (`AccountTransaction.invoices[]`).
 */
export const invoiceCaptureLegsAdminAPISchema = z.object({
  legs: z
    .array(
      z.object({
        orderId: objectIdSchema,
        contractId: objectIdSchema,
      }),
    )
    .min(1),
  /**
   * If true, the capture ignores any active `Agreement` between the
   * leg's buyer/seller pair (uses the request date for the period
   * instead of the agreement's invoice issuance schedule). Defaults
   * to false — matches the existing per-leg capture semantics.
   */
  ignoreAgreement: z.boolean().optional(),
});
export type InvoiceCaptureLegsAdminAPI = API<
  z.infer<typeof invoiceCaptureLegsAdminAPISchema>,
  {
    created: Invoice[];
    skipped: Array<{ orderId: Id; contractId: Id; reason: string }>;
  }
>;

// ======== eligible legs (admin) ========

/**
 * List delivered legs that are eligible to be captured into a new
 * invoice for the requested counterparty pair. The result feeds the
 * "Eligible orders" multi-select in `ClientDrawer`'s Invoices tab.
 *
 * Eligibility (all must hold):
 *   - Order is in a delivered state.
 *   - Leg (`Order.contracts[i]`) is `accepted` or `completed`.
 *   - Leg has no invoice yet (`Order.contracts[i].invoices[]` empty).
 *   - Either `orgId` or `clientId` matches the leg's buyer or seller
 *     (one of the filters must be set; both together = AND).
 */
export const invoiceEligibleLegsAdminAPISchema = z.object({
  /** the Signed Org on either side of the leg */
  orgId: objectIdSchema.optional(),
  /** the CRM Client on either side of the leg */
  clientId: objectIdSchema.optional(),
  /** restrict to legs where the orgId/clientId is in this role */
  asRole: z.enum(['buyer', 'seller', 'any']).optional(),
});
/**
 * Single eligible leg as returned by both the admin and party-
 * facing endpoints. Keeps display data the picker needs (route,
 * pickup, fee summary) so the UI doesn't have to N+1-fetch each
 * order. Sorted server-side by `pickupAt` ascending (oldest
 * first) so the user works through the backlog naturally.
 */
export type EligibleLeg = {
  orderId: Id;
  contractId: Id;
  orderRefMain: string;
  orderRefShort: string;
  buyerName: string;
  sellerName: string;
  currency: Currency;
  /** Contract-leg total — currency-typed; the picker renders it via `<Price />`. */
  legTotal: number;
  /**
   * Set when at least one fee is applied on the leg
   * (`contract.amount.fees.applied.length > 0`). Carries the
   * count and the rolled-up fee subtotal so the card can render
   * a "+ fees" tick next to the price without re-querying the
   * order. Undefined when no fees are applied (i.e. don't render
   * the chip).
   */
  fees?: { total: number; count: number };
  /** `Order.dates.pickup.from` — used for card display + server-side sort. */
  pickupAt: string | Date | undefined;
  /** Display name of the pickup city (already localised by the backend). */
  pickupCity?: string;
  /** Fallback full-address line when no city is known. */
  pickupAddress?: string;
  /** Display name of the final dropoff city. */
  dropoffCity?: string;
  /** Fallback full-address line for the dropoff. */
  dropoffAddress?: string;
  deliveredAt: string | Date | undefined;
  /**
   * `true` when the order's `settings.podCopy` includes
   * `'return_original_pod'` (the paper-original-POD requirement
   * from the client/org default). Drives the create-invoice
   * picker's default `originalCopyStatus` and the badge tone.
   */
  requiresOriginalCopy: boolean;
  /**
   * Lifecycle status of the order's paper-POD trail
   * (`Order.status.podCopy`), surfaced verbatim so the picker
   * can render a tiny status chip on each card without re-
   * fetching the full Order doc.
   */
  podStatus: OrderPODStatus;
};

export type InvoiceEligibleLegsAdminAPI = API<
  z.infer<typeof invoiceEligibleLegsAdminAPISchema>,
  {
    legs: EligibleLeg[];
  }
>;

// ======== eligible legs (current user, seller-side) ========

/**
 * Party-facing twin of {@link invoiceEligibleLegsAdminAPISchema}. The
 * backend pins the seller side to the viewer's org and (optionally)
 * narrows the buyer side to a single CRM `Client`. Used by the
 * "Create invoice" picker on the carrier-dashboard CRM Invoices
 * tab so a carrier can turn delivered legs into invoices for
 * themselves without needing super-admin access.
 *
 * No `orgId` field on the request — the viewer is always the
 * seller, resolved from the session by `CurrentUserService`. No
 * `asRole` either; this endpoint is implicitly `asRole: 'seller'`.
 */
export const invoiceEligibleLegsCurrentUserAPISchema = z.object({
  /** restrict eligible legs to those where this CRM client is the buyer */
  clientId: objectIdSchema.optional(),
});
export type InvoiceEligibleLegsCurrentUserAPI = API<
  z.infer<typeof invoiceEligibleLegsCurrentUserAPISchema>,
  InvoiceEligibleLegsAdminAPI['data']
>;

// ======== capture legs (current user, seller-side) ========

/**
 * Party-facing twin of {@link invoiceCaptureLegsAdminAPISchema}.
 *
 * Unlike the admin endpoint (which creates one invoice per leg
 * for ledger-style auditing), this party-facing path bundles
 * **all** selected legs into a **single** `Invoice` whose
 * `items[]` carries one row per leg. That matches how a seller
 * thinks about invoicing in practice: "I have 5 delivered
 * orders for this client; one invoice, please."
 *
 * The backend enforces:
 *   - Viewer is the seller on every leg (else the leg lands in
 *     `skipped[]` with `reason: 'not_seller'`).
 *   - All eligible legs share the same buyer + seller +
 *     currency. Cross-counterparty / cross-currency bundling is
 *     rejected — the picker is Client-scoped so this should
 *     never trip in practice.
 *
 * The per-leg overrides (date / number / copy status) that
 * existed in the original plan are gone — these were always
 * going to be the same across the batch, so they live at the
 * request root instead.
 */
export const invoiceCaptureLegsCurrentUserAPISchema = z.object({
  /**
   * Legs to bundle into ONE invoice. Must share buyer + seller
   * + currency (enforced server-side); the picker only ever
   * surfaces legs that satisfy this because it's Client-scoped.
   */
  legs: z
    .array(
      z.object({
        orderId: objectIdSchema,
        contractId: objectIdSchema,
      }),
    )
    .min(1),
  /**
   * Optional override of the new Invoice's `period.due`. When
   * omitted, the DTO falls back to the matching Agreement's
   * payment-terms schedule (or the platform default). The UI
   * deliberately doesn't auto-fill it so the seller can leave it
   * blank and accept whatever the agreement says.
   */
  expectedDueDate: dateSchema.optional(),
  /**
   * Optional accounting reference for the invoice. Lands in
   * `invoiceProof.refNumber` as a create-time shell (target =
   * `'seller_proof'`, status = `'pending'`); the actual VAT
   * image gets attached later via the upload-proof drawer.
   */
  invoiceNumber: z.string().trim().optional(),
  /**
   * Optional override of the lifecycle status for the original-
   * copy flow on the created invoice. Defaults from the legs'
   * orders' `settings.podCopy` (any leg requiring
   * `'return_original_pod'` → `'not_received'`; otherwise
   * `'no_pod_required'`).
   */
  originalCopyStatus: orderPODCopyStatusSchema.optional(),
});
export type InvoiceCaptureLegsCurrentUserAPI = API<
  z.infer<typeof invoiceCaptureLegsCurrentUserAPISchema>,
  {
    /**
     * The single invoice created for the batch. `undefined` if
     * every leg got skipped (e.g. all already captured).
     */
    created: Invoice | undefined;
    /** Per-leg skip reasons (`not_seller`, `already_captured`, etc.). */
    skipped: Array<{ orderId: Id; contractId: Id; reason: string }>;
  }
>;

// ======== update invoice original-copy status (current user) ========

/**
 * Mark the paper-original-invoice flow on a single invoice.
 * Used by the seller (the receivable holder, who's responsible
 * for sending the paper copy) and the buyer (who marks
 * `received` when the copy lands). The backend gates writes by
 * party.
 *
 * Stub: the API surface exists so the front-end "mark sent" /
 * "mark received" buttons can land later without another
 * schema bump.
 */
export const invoiceOriginalCopyStatusUpdateAPISchema = z.object({
  invoiceId: objectIdSchema,
  status: orderPODCopyStatusSchema,
});
export type InvoiceOriginalCopyStatusUpdateAPI = API<
  z.infer<typeof invoiceOriginalCopyStatusUpdateAPISchema>,
  Invoice
>;

// ======== calc multi invoices ========

/** Request schema for calculating totals of multiple selected invoices. */
export const calcMultiInvoicesAPISchema = z.object({
  invoiceIds: z.array(objectIdSchema).min(1),
});

/** Calculate totals for multiple selected invoices (amounts, breakdown, cashback). */
export type CalcMultiInvoicesAPI = API<
  z.infer<typeof calcMultiInvoicesAPISchema>,
  {
    invoices: Invoice[];
    amount: {
      total: number;
      paid: number;
      remaining: number;
      currency: Currency;
    };
    breakdown: Array<{
      _id: Id;
      refMain: string;
      total: number;
      paid: number;
      remaining: number;
      cashback?: {
        percentage: number;
        amount: number;
        expiresAt: string;
        isEligible: boolean;
      };
    }>;
    skipped: Array<{ _id: Id; refMain: string; status: string }>;
  }
>;

// ======== get all unpaid invoices ========

/** Fetch ALL unpaid/partially_paid invoices for the current user (no pagination, sorted by due date ascending). */
export type GetAllUnpaidInvoicesAPI = API<void, Invoice[]>;

// ======== get unpaid total ========

/** Aggregated total of all unpaid/partially_paid invoices for the current user. */
export type GetUnpaidTotalAPI = API<void, { total: number; count: number; currency: Currency }>;
